<template>
    <div class="cb-wizard-step1 cb-wizard__container">
        <div class="cb-wizard__body">
            <div class="cb-wizard__header">
                <h3 class="cb-wizard__title">Find your keywords</h3>
                <Stepper class="cb-wizard__stepper"
                    :labels="['Step1', 'Step2', 'Step3', 'Step4', 'Step5']"
                    :currentStep="0" />
            </div>
            <div class="cb-wizard-step1__body">
                <div class="cb-wizard-step1__form-holder">
                    <div class="cb-wizard-step1__url">
                        <label class="cb-wizard-step1__field">
                            <span class="cb-wizard-step1__label">Enter a placeholder URL for your new concept</span>
                            <base-smart-input-autocomplete easyModel
                                ref="selected-url"
                                class="cb-wizard-step1__input"
                                :options="autocompleteMappedUrls"
                                :isNotValid="!!urlFieldError"
                                v-model="selectedUrl"
                                placeholder="Mapped URL">
                                <base-material-design-icon class="cb-wizard-step1__clear-url"
                                    v-if="selectedUrl"
                                    @click.stop="clearSelectedUrl"
                                    icon="close" />
                            </base-smart-input-autocomplete>
                            <span v-if="urlFieldError"
                                class="cb-wizard-step1__error">{{ urlFieldError }}</span>
                        </label>
                    </div>
                    <form class="cb-wizard-step1__keyword"
                        @submit.prevent="searchKeywords()">
                        <label class="cb-wizard-step1__field">
                            <span class="cb-wizard-step1__label">Enter your starter keyword</span>
                            <base-smart-input-search class="cb-wizard-step1__input cb-wizard-step1__input_keyword"
                                easyModel
                                ref="searchInput"
                                :allowFreeText="true"
                                @change="handleKeywordChange($event)"
                                v-model="selectedKeyword"
                                placeholder="Enter a keyword">
                            </base-smart-input-search>
                        </label>
                        <button class="cb-wizard__button cb-wizard-step1__submit"
                            type="submit"
                            :disabled="!canSearchKeywords">Find Keywords</button>
                    </form>
                </div>
                <div v-if="isDataLoading || isInnerDataLoading || !domains.length"
                    class="cb-wizard-step1__loader">
                    <base-loader />
                </div>
            </div>
        </div>
        <div class="cb-wizard__sidebar">
            <span class="cb-wizard__sidebar-title">Discover Supporting Keywords</span>
            <p class="cb-wizard__sidebar-text">To support your Starter Keyword, it’s helpful to incorporate additional
                related keywords to your page concept mapping. This helps to understand the ways and different
                terminology your target audience may use related to this content topic.</p>
            <p class="cb-wizard__sidebar-text">You can have up to 25 keywords for planning this content brief, however
                it is not necessary to use all 25.</p>
            <span class="cb-wizard__sidebar-pro-tip">Pro tip: <base-material-design-icon
                    icon="lightBulbOutline" /></span>
            <p class="cb-wizard__sidebar-text">Use a more specific keyword phrase as your starter keyword: 3-5+ word
                phrases help ensure more specific and useful keyword recommendations.</p>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import Stepper from './stepper.vue';
const urlValidationErrors = {
    alreadyExists: 'URL already exists',
    notValid: 'URL is not valid'
}

export default {
    name: 'ContentBriefWizardStep1',
    props: {
        url: String,
        keyword: String,
        domains: Array,
        urls: Array,
        isDataLoading: Boolean,
    },
    components: {
        Stepper
    },
    data() {
        return {
            selectedUrl: this.url,
            isInnerDataLoading: false,
            selectedKeyword: this.keyword,
            appliedKeyword: '',
            isLoading: false,
            urlFieldError: '',
        }
    },
    watch: {
        selectedUrl() {
            this.urlFieldError = '';
        },
    },
    computed: {
        domainOptions() {
            return this.domains.map(domain => ({
                label: domain,
                value: domain
            }));
        },
        autocompleteMappedUrls() {
            if (!this.domainOptions || !this.domainOptions.length) {
                return []
            }
            return this.domainOptions?.filter(v => v.label.includes(this.selectedUrl))
        },
        selectedUrlId() {
            return this.domainOptions?.find(v => v.label === this.selectedUrl)?.value
        },
        trimmedKeyword() {
            return this.selectedKeyword.trim()
        },
        canSearchKeywords() {
            return !!this.selectedUrl && !!this.trimmedKeyword.length && this.trimmedKeyword !== this.appliedKeyword
        },
    },
    methods: {
        ...mapActions('keywordCensus', [
            'getKeywordsDiscovery',
        ]),
        clearSelectedUrl() {
            this.selectedUrl = ''
            const input = this.$refs['selected-url']
            if (input) {
                input.clear()
            }
        },
        close() {
            this.$emit('close')
        },
        async searchKeywords() {
            const urlPattern = /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
            if (!urlPattern.test(this.selectedUrl)) {
                this.urlFieldError = urlValidationErrors.notValid;
                return;
            }

            const matchingDomains = this.domains.find(domain => this.selectedUrl.toLowerCase() === domain.toLowerCase());
            const matchingURLs = this.urls.find(url => this.selectedUrl.toLowerCase() === url.toLowerCase());

            if (matchingDomains || matchingURLs) {
                this.urlFieldError = urlValidationErrors.alreadyExists;
                return;
            }

            if (!this.canSearchKeywords) {
                return
            }

            this.$refs.searchInput.blur()
            this.isInnerDataLoading = true
            this.appliedKeyword = this.trimmedKeyword
            const { data, errors } = await this.getKeywordsDiscovery({
                clientId: this.clientId,
                keyword: this.trimmedKeyword
            });
            this.isInnerDataLoading = false
            if (errors.length) {
                console.log(errors);
                return;
            }

            this.keywordsList = data;
            this.$emit('keywordsFound', { keywords: this.keywordsList, url: this.selectedUrl, keyword: this.selectedKeyword });
        },
        handleKeywordChange(data) {
            this.selectedKeyword = typeof data === 'string' ? data : data.text
        },

    },
}
</script>

<style scoped
    lang="scss">
    .cb-wizard-step1 {
        max-height: 95vh;
        box-sizing: border-box;

        &__body {
            display: flex;
            flex: 1 1 100%;
            justify-content: center;
            flex-direction: column;
            position: relative;
            margin: 0 -10px;
            padding: 10px;
            max-height: calc(100vh - 250px);
        }

        &__loader {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, .1);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &__form-holder {
            margin: 0 auto;
            max-width: 480px;
            width: 100%;
        }

        &__url,
        &__keyword {
            margin-bottom: 20px;
        }

        &__field {
            display: block;
            position: relative;
            padding-top: 20px;

            &:first-child {
                padding-top: 0;
            }
        }

        &__input {
            height: 58px !important;
            box-sizing: border-box;
            width: 100%;

            &_dummy {
                border: 2px solid #e7e9eb;
                border-radius: 4px;
                padding: 0 20px;
                font-weight: 500;
                outline: none !important;
            }
        }

        &__label {
            display: block;
            font-weight: 600;
            margin-bottom: 5px;

            &_thin {
                font-weight: 400;
            }
        }

        &__clear-url {
            padding-right: 5px;
        }

        &__error {
            color: $rfx-light-red;
        }

        &__content {
            flex: 0 1;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }

        &__submit {
            margin: 20px 0 0 auto;
        }
    }
</style>